// App.js
import React, { useState, useEffect } from "react";
import "./App.css";

// (services)
import { fetchAaveReserves } from "./services/aaveService";
import { fetchAllVaults } from "./services/vaultsService";

// (config)
import {
  stableSymbols,
  ethSymbols,
  allSymbols,
  stableVaultAddressesByChain,
  ethVaultAddressesByChain,
} from "./config";

// (components)
import Header from "./components/Header";
import Dashboard from "./components/Dashboard";
import CollateralTable from "./components/CollateralTable";
import BorrowTable from "./components/BorrowTable";

export default function App() {
  // ----------------------
  // 1) State
  // ----------------------
  const [network, setNetwork] = useState("mainnet");
  const [allReserves, setAllReserves] = useState([]);
  const [aaveLoading, setAaveLoading] = useState(true);
  const [aaveError, setAaveError] = useState(null);

  const [allVaults, setAllVaults] = useState([]);
  const [allVaultsLoading, setAllVaultsLoading] = useState(true);
  const [allVaultsError, setAllVaultsError] = useState(null);

  const [vaultSortColumn, setVaultSortColumn] = useState(null);
  const [vaultSortDirection, setVaultSortDirection] = useState("asc");
  const [borrowSortColumn, setBorrowSortColumn] = useState(null);
  const [borrowSortDirection, setBorrowSortDirection] = useState("asc");

  const [timeframe, setTimeframe] = useState("30day");

  const [selectedVault, setSelectedVault] = useState(null);
  const [selectedAave, setSelectedAave] = useState(null);

  const [ltv, setLtv] = useState("0.0");

  const [bestNetApyResults, setBestNetApyResults] = useState([]);
  const [bestNetApyIndex, setBestNetApyIndex] = useState(0);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 100); // Trigger at 100px scroll
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  // ----------------------
  // 2) Fetch Aave Reserves
  // ----------------------
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize); // Cleanup
  }, []);

  useEffect(() => {
    async function loadAave() {
      try {
        setAaveLoading(true);
        setAaveError(null);
        const apiKey = process.env.REACT_APP_THEGRAPH_API_KEY;
        const data = await fetchAaveReserves(apiKey, network);
        setAllReserves(data);
      } catch (err) {
        setAaveError(err.message);
      } finally {
        setAaveLoading(false);
      }
    }
    loadAave();
  }, [network]);

  // -----------------------------
  // 3) Fetch All Vaults
  // -----------------------------
  useEffect(() => {
    async function loadAllVaults() {
      try {
        setAllVaultsLoading(true);
        setAllVaultsError(null);

        const vaultApiKey = process.env.REACT_APP_VAULTS_FYI_KEY;
        const stableAddressesArr = stableVaultAddressesByChain[network] || [];
        const ethAddressesArr = ethVaultAddressesByChain[network] || [];

        const [stableData, ethData] = await Promise.all([
          fetchAllVaults(stableAddressesArr, vaultApiKey, network),
          fetchAllVaults(ethAddressesArr, vaultApiKey, network),
        ]);

        const stableWithType = stableData.map((v) => ({
          ...v,
          type: "stable",
        }));
        const ethWithType = ethData.map((v) => ({ ...v, type: "eth" }));

        setAllVaults([...stableWithType, ...ethWithType]);
      } catch (err) {
        setAllVaultsError(err.message);
      } finally {
        setAllVaultsLoading(false);
      }
    }
    loadAllVaults();
  }, [network]);

  // ----------------------
  // 4) Helpers
  // ----------------------
  function parseAaveBorrowRate(reserve) {
    const raw = Number(reserve.variableBorrowRate) || 0;
    return raw / 1e27;
  }

  function parseVaultApy(vault) {
    const raw = Number(vault.apy?.total?.[timeframe] ?? 0);
    return raw / 1e4;
  }

  function computeNetApyDecimal(vault, reserve) {
    if (!vault || !reserve) return null;
    const borrowDecimal = parseAaveBorrowRate(reserve);
    const supplyDecimal = parseVaultApy(vault);
    const ltvRatio = Math.min(parseFloat(ltv) || 0, 100) / 100;
    return supplyDecimal - borrowDecimal * ltvRatio;
  }

  function getNetApy() {
    if (!selectedVault || !selectedAave) return null;
    const val = computeNetApyDecimal(selectedVault, selectedAave);
    if (val == null) return null;
    return (val * 100).toFixed(2) + "%";
  }

  function getLeveragedApy() {
    if (!selectedVault || !selectedAave) return null;
    const val = computeNetApyDecimal(selectedVault, selectedAave);
    if (val == null) return null;
    const ltvRatio = Math.min(parseFloat(ltv) || 0, 100) / 100;
    const denom = 1 - ltvRatio;
    if (denom <= 0) return "∞%";
    return ((val / denom) * 100).toFixed(2) + "%";
  }

  // ----------------------
  // 5) Sorting Logic
  // ----------------------
  function toggleSortDirection(dir) {
    return dir === "asc" ? "desc" : "asc";
  }

  function handleVaultSort(column) {
    if (vaultSortColumn === column) {
      setVaultSortDirection(toggleSortDirection(vaultSortDirection));
    } else {
      setVaultSortColumn(column);
      setVaultSortDirection("asc");
    }
  }

  function handleBorrowSort(column) {
    if (borrowSortColumn === column) {
      setBorrowSortDirection(toggleSortDirection(borrowSortDirection));
    } else {
      setBorrowSortColumn(column);
      setBorrowSortDirection("asc");
    }
  }

  function sortVaults(data) {
    if (!vaultSortColumn) return data;
    return [...data].sort((a, b) => {
      let valA = 0;
      let valB = 0;
      if (vaultSortColumn === "apy") {
        valA = parseVaultApy(a);
        valB = parseVaultApy(b);
      } else if (vaultSortColumn === "tvl") {
        valA = parseFloat(a.tvlDetails?.tvlUsd ?? "0");
        valB = parseFloat(b.tvlDetails?.tvlUsd ?? "0");
      }
      return vaultSortDirection === "asc" ? valA - valB : valB - valA;
    });
  }

  function sortBorrowReserves(data) {
    if (!borrowSortColumn) return data;
    return [...data].sort((a, b) => {
      const valA = parseAaveBorrowRate(a);
      const valB = parseAaveBorrowRate(b);
      return borrowSortDirection === "asc" ? valA - valB : valB - valA;
    });
  }

  // ----------------------
  // 6) Best Net APY logic
  // ----------------------
  function handleFindBestNetApy(strategy) {
    if (!strategy) {
      alert(
        "Please specify a strategy (e.g., 'stable-stable', 'eth-eth', etc.)."
      );
      return;
    }

    if (allVaults.length === 0 || allReserves.length === 0) {
      alert("No data available to find Best Net APY.");
      return;
    }

    const effectiveLtv = parseFloat(ltv) || 90;
    setLtv(effectiveLtv.toString());
    const ltvRatio = Math.min(effectiveLtv, 100) / 100;

    let filteredVaults = [];
    let filteredReserves = [];
    switch (strategy) {
      case "stable-stable":
        filteredVaults = allVaults.filter((v) =>
          stableSymbols.includes(v.token?.symbol)
        );
        filteredReserves = allReserves.filter((r) =>
          stableSymbols.includes(r.symbol)
        );
        break;
      case "eth-eth":
        filteredVaults = allVaults.filter((v) =>
          ethSymbols.includes(v.token?.symbol)
        );
        filteredReserves = allReserves.filter((r) =>
          ethSymbols.includes(r.symbol)
        );
        break;
      case "eth-long":
        filteredVaults = allVaults.filter((v) =>
          ethSymbols.includes(v.token?.symbol)
        );
        filteredReserves = allReserves.filter((r) =>
          stableSymbols.includes(r.symbol)
        );
        break;
      case "eth-short":
        filteredVaults = allVaults.filter((v) =>
          stableSymbols.includes(v.token?.symbol)
        );
        filteredReserves = allReserves.filter((r) =>
          ethSymbols.includes(r.symbol)
        );
        break;
      default:
        alert("Invalid strategy selected.");
        return;
    }

    if (filteredVaults.length === 0 || filteredReserves.length === 0) {
      alert("No valid pairs found for the selected strategy.");
      return;
    }

    const combinations = [];
    for (const vault of filteredVaults) {
      for (const reserve of filteredReserves) {
        const vaultApy = parseVaultApy(vault);
        const borrowRate = parseAaveBorrowRate(reserve);
        const netApyDecimal = vaultApy - borrowRate * ltvRatio;
        if (netApyDecimal != null) {
          combinations.push({
            vault,
            reserve,
            netApyDecimal,
            vaultApy,
            borrowRate,
          });
        }
      }
    }

    if (combinations.length === 0) {
      alert("No valid combinations could be calculated.");
      return;
    }

    combinations.sort((a, b) => b.netApyDecimal - a.netApyDecimal);

    const uniqueVaultCombinations = [];
    const seenVaults = new Set();
    for (const combo of combinations) {
      const vaultAddress = combo.vault.address;
      if (!seenVaults.has(vaultAddress) || uniqueVaultCombinations.length < 5) {
        uniqueVaultCombinations.push(combo);
        seenVaults.add(vaultAddress);
      }
      if (uniqueVaultCombinations.length >= Math.min(10, combinations.length))
        break;
    }

    console.log(`Total Combinations: ${combinations.length}`);
    console.log(
      "Top Combinations:",
      combinations.slice(0, 5).map((c) => ({
        vault: c.vault.token?.symbol,
        reserve: c.reserve.symbol,
        netApy: (c.netApyDecimal * 100).toFixed(2) + "%",
        vaultApy: (c.vaultApy * 100).toFixed(2) + "%",
        borrowRate: (c.borrowRate * 100).toFixed(2) + "%",
      }))
    );
    console.log(
      "Top Unique Vault Combinations:",
      uniqueVaultCombinations.map((c) => ({
        vault: c.vault.token?.symbol,
        reserve: c.reserve.symbol,
        netApy: (c.netApyDecimal * 100).toFixed(2) + "%",
      }))
    );

    const finalResults = combinations;
    setBestNetApyResults(finalResults);
    setBestNetApyIndex(0);
    setSelectedVault(finalResults[0].vault);
    setSelectedAave(finalResults[0].reserve);
  }

  function handleNextBest() {
    if (bestNetApyIndex >= bestNetApyResults.length - 1) return;
    const newIndex = bestNetApyIndex + 1;
    const pair = bestNetApyResults[newIndex];
    setSelectedVault(pair.vault);
    setSelectedAave(pair.reserve);
    setBestNetApyIndex(newIndex);
  }

  function handlePrevBest() {
    if (bestNetApyIndex <= 0) return;
    const newIndex = bestNetApyIndex - 1;
    const pair = bestNetApyResults[newIndex];
    setSelectedVault(pair.vault);
    setSelectedAave(pair.reserve);
    setBestNetApyIndex(newIndex);
  }

  function handleReset() {
    setSelectedVault(null);
    setSelectedAave(null);
    setLtv("0.0");
    setBestNetApyResults([]);
    setBestNetApyIndex(0);
  }

  // ----------------------
  // 7) Derived Data
  // ----------------------
  const filteredAaveReserves = allReserves.filter((r) =>
    allSymbols.includes(r.symbol)
  );
  const sortedBorrowData = sortBorrowReserves(filteredAaveReserves);
  const sortedVaultData = sortVaults(allVaults);

  const netApyStr = getNetApy();
  const leveragedApyStr = getLeveragedApy();

  // Row clicks
  const handleVaultRowClick = (vault) => {
    if (selectedVault && selectedVault.address === vault.address) {
      setSelectedVault(null);
    } else {
      setSelectedVault(vault);
    }
  };
  const handleReserveRowClick = (reserve) => {
    if (selectedAave && selectedAave.id === reserve.id) {
      setSelectedAave(null);
    } else {
      setSelectedAave(reserve);
    }
  };

  if (windowWidth < 1280) {
    return (
      <div className="screen-size-warning">
        {windowWidth >= 900 ? (
          <p>
            Gm. Screen’s a bit small—try ⌘ - a couple times to fit the full
            vibe.
          </p>
        ) : (
          <p>Gm. Please bump it up to 1280px or higher for the full vibe!</p>
        )}
      </div>
    );
  }

  return (
    <div className="app-root">
      {/* 1) Header */}
      <Header
        network={network}
        onNetworkChange={setNetwork}
        isScrolled={isScrolled}
      />

      {/* 2) Dashboard */}
      <Dashboard
        timeframe={timeframe}
        setTimeframe={setTimeframe}
        selectedVault={selectedVault}
        setSelectedVault={setSelectedVault}
        selectedAave={selectedAave}
        setSelectedAave={setSelectedAave}
        parseVaultApy={parseVaultApy}
        parseAaveBorrowRate={parseAaveBorrowRate}
        handleFindBestNetApy={handleFindBestNetApy}
        handlePrevBest={handlePrevBest}
        handleNextBest={handleNextBest}
        bestNetApyIndex={bestNetApyIndex}
        bestNetApyResults={bestNetApyResults}
        ltv={ltv}
        setLtv={setLtv}
        netApyStr={netApyStr}
        leveragedApyStr={leveragedApyStr}
        isScrolled={isScrolled}
      />

      {/* 3) Main Body: wrap in "app-body" for correct styling */}
      <div className="app-body">
        <div className="collateral-and-borrow-wrapper">
          <CollateralTable
            allVaultsLoading={allVaultsLoading}
            allVaultsError={allVaultsError}
            sortedVaultData={sortedVaultData}
            selectedVault={selectedVault}
            onVaultRowClick={handleVaultRowClick}
            onVaultSort={handleVaultSort}
            vaultSortColumn={vaultSortColumn}
            vaultSortDirection={vaultSortDirection}
            timeframe={timeframe}
          />

          <BorrowTable
            aaveLoading={aaveLoading}
            aaveError={aaveError}
            sortedBorrowData={sortedBorrowData}
            selectedAave={selectedAave}
            onReserveRowClick={handleReserveRowClick}
            onBorrowSort={handleBorrowSort}
            borrowSortColumn={borrowSortColumn}
            borrowSortDirection={borrowSortDirection}
            parseAaveBorrowRate={parseAaveBorrowRate}
          />
        </div>
      </div>

      {/* 4) APY display / bottom area */}
      {/* 5) Best Net APY & Reset Buttons */}
    </div>
  );
}
